import { useEffect, useState } from 'react'
import { Flex, Spinner } from '@radix-ui/themes'
import { Link, useParams } from '@tanstack/react-router'
import { IconSearch } from '@tabler/icons-react'
import { useGetClassStudent } from '@/services/classDetailService'
import { useClassStore } from '@/stores/useClassStore'
import { cn } from '@/lib/utils'
import { getMarksPercentage, getMarksStyle } from '@/utils/style'
import { formatMinutes } from '@/utils/time'
import { useDebounce } from '@/hooks/useDebounce'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import TablePaginate from '@/components/ui/table-paginate'

const StudentTable = () => {
  const { classId } = useParams({
    from: '/_authenticated/class/$classId/$moduleId',
  })
  const { setSelectedUserRole } = useClassStore()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')

  const debouncedQuery = useDebounce(search)

  const { students, isLoadingClassStudents, meta } = useGetClassStudent({
    classId,
    page,
    limit: 10,
    search: debouncedQuery.trim(),
  })

  const handlePageChange = (page: number) => {
    setPage(page)
  }

  useEffect(() => {
    setPage(1)
  }, [debouncedQuery])

  return (
    <Card className='right-0 left-0 z-100 py-1'>
      <CardContent className='p-0'>
        <div className={'space-y-6 rounded-lg bg-[#FFFFFF] p-4'}>
          <div className='flex items-center justify-between'>
            <p className='text-2xl font-[400] text-[#002652]'>Student List</p>
            <div className='relative'>
              <IconSearch className='text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2' />
              <Input
                placeholder={'Search'}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className='h-10 w-[150px] pl-9 lg:w-[250px]'
              />
            </div>
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow className='bg-[#F2F4F7]'>
              <TableHead className='font-mundial p-4 text-sm font-[600] text-[#002652]'>
                Name
              </TableHead>
              <TableHead className='font-mundial text-center text-sm font-[600] text-[#002652]'>
                Mock Exam Attempts
              </TableHead>
              <TableHead className='font-mundial text-center text-sm font-[600] text-[#002652]'>
                Practice Test Attempts
              </TableHead>

              <TableHead className='font-mundial text-center text-sm font-[600] text-[#002652]'>
                Practice Duration
              </TableHead>
              <TableHead className='font-mundial text-center text-sm font-[600] text-[#002652]'>
                Average Mark
              </TableHead>
              <TableHead className='font-mundial text-center text-sm font-[600] text-[#002652]' />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoadingClassStudents ? (
              <TableRow>
                <TableCell colSpan={8} className='h-24 justify-center'>
                  <Flex justify={'center'}>
                    <Spinner size={'3'} />
                  </Flex>
                </TableCell>
              </TableRow>
            ) : (
              <>
                {students?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className='h-24 text-center'>
                      No data found.
                    </TableCell>
                  </TableRow>
                )}
                {students?.map((item) => (
                  <TableRow key={item.user_id} className='hover:bg-gray-50'>
                    <TableCell className='w-[167px] truncate p-4'>
                      {item.student_name}
                    </TableCell>
                    <TableCell className='w-[250px] text-center text-sm font-[300] text-[#002652]'>
                      {item.me_attempts}
                    </TableCell>
                    <TableCell className='w-[250px] text-center text-sm font-[300] text-[#002652]'>
                      {item.pt_attempts}
                    </TableCell>
                    <TableCell className='w-[250px] text-center text-sm font-[300] text-[#002652]'>
                      {formatMinutes(item.total_duration_minutes) ?? 0}
                    </TableCell>
                    <TableCell
                      className={cn(
                        'w-[250px] truncate text-center text-sm font-[300] text-[#002652]',
                        getMarksStyle(item.avg_marks)
                      )}
                    >
                      {getMarksPercentage(item.avg_marks)}
                    </TableCell>

                    <TableCell className='w-[119px] px-3'>
                      <Link
                        to='/users/$studentId'
                        params={{ studentId: item.user_id.toString() }}
                        search={{ source: 'fromClassDetail' }}
                        className='h-auto rounded px-4 py-2 font-medium text-[#0057B8] hover:bg-[#0057B8]/10 hover:text-blue-800'
                        onClick={() => setSelectedUserRole('student')}
                      >
                        View detail
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </>
            )}
          </TableBody>
        </Table>

        {meta && meta.total_pages > 1 && (
          <div className='flex justify-end p-4'>
            <TablePaginate
              itemPerPage={meta.items_per_page}
              totalItems={meta.total_items}
              page={page}
              totalPages={meta.total_pages}
              onPageChange={handlePageChange}
              readOnlyPageDisplay={false}
              hideOnSinglePage={false}
            />
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default StudentTable
